import { useTheme } from "../context/ThemeContext";
import { updateAssessmentStatus } from "../api/client";
import { needsPreSurveyBeforeFull } from "../utils/preSurvey";
import { assessmentIntroMeta } from "../utils/assessmentIntroMeta";
import { trackLabel as typeTrackLabel, welcomeDescription, productName } from "../utils/participantAssessmentType";
import { brandColor } from "../utils/participantBranding";

/* ─── icons ─────────────────────────────────────────────────────────────── */
const IconDoc = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0119 9.414V19a2 2 0 01-2 2z" />
  </svg>
);
const IconClock = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconSparkle = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
  </svg>
);
const IconCheck = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
const IconArrow = () => (
  <svg
    className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);
const IconSun = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const IconMoon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

export default function WelcomePage({
  surveyToken,
  userData,
  companyName,
  branding = null,
  assessmentTrack = "lite",
  inviteFlow = "v2_lite",
  questionCount = null,
  assessmentType,
  onStart,
  onPreSurvey,
}) {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";
  const accent = brandColor(branding);
  const tenantLogo = branding?.logo_url || null;

  const handleStart = async () => {
    try {
      await updateAssessmentStatus(surveyToken, "Pending");
    } catch { /* continue */ }
    onStart();
  };

  const showPreSurvey = needsPreSurveyBeforeFull({
    userData,
    assessmentTrack,
    inviteFlow,
  });

  const isLegacyFull         = inviteFlow === "v1_full";
  const isV2FullContinuation = inviteFlow === "v2_full_continuation";

  const fallbackCount =
    assessmentTrack === "lite" ? 10 : isV2FullContinuation ? 55 : 65;
  const { label: trackLabel, questions, minutes } = assessmentIntroMeta({
    track: assessmentTrack === "lite" ? "lite" : "full",
    questionCount: questionCount ?? fallbackCount,
    inviteFlow,
  });

  const firstName = userData?.name
    ? userData.name.trim().split(/\s+/)[0]
    : null;

  const ctaLabel = isV2FullContinuation ? "Continue assessment" : "Begin assessment";
  const showLiteUpgradeNote =
    assessmentTrack === "lite" && !isLegacyFull && !isV2FullContinuation;

  return (
    <>
      <style>{`
        @keyframes orb-drift {
          0%,100% { transform:translate(0,0) scale(1); }
          33%      { transform:translate(40px,-30px) scale(1.08); }
          66%      { transform:translate(-20px,20px) scale(0.95); }
        }
        @keyframes orb-drift-2 {
          0%,100% { transform:translate(0,0) scale(1); }
          40%     { transform:translate(-50px,30px) scale(1.1); }
          70%     { transform:translate(30px,-20px) scale(0.92); }
        }
        @keyframes wu-fade-up {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position:-200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pulse-dot {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.45; transform:scale(0.8); }
        }
        .wu-a  { animation:wu-fade-up .45s cubic-bezier(.22,.68,0,1.2) both; }
        .wu-a1 { animation-delay:.06s; }
        .wu-a2 { animation-delay:.12s; }
        .wu-a3 { animation-delay:.19s; }
        .wu-a4 { animation-delay:.26s; }
        .cta-shimmer {
          background:linear-gradient(105deg,${accent} 0%,#105a7c 40%,#1a7fa8 50%,#105a7c 60%,${accent} 100%);
          background-size:200% auto;
          animation:shimmer 3s linear infinite;
        }
        .cta-shimmer-dark  { box-shadow:0 0 28px rgba(0,79,115,.6),0 2px 8px rgba(0,0,0,.35); }
        .cta-shimmer-light { box-shadow:0 4px 18px rgba(0,79,115,.35); }
        .cta-shimmer:hover { filter:brightness(1.09); }
        .pulse-dot { animation:pulse-dot 1.8s ease-in-out infinite; }
      `}</style>

      {/* ── full-bleed background ─────────────────────────────────────────── */}
      <div
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-4 transition-colors duration-500"
        style={{
          background: dark
            ? "linear-gradient(135deg,#04080f 0%,#070e1c 50%,#05111f 100%)"
            : "linear-gradient(135deg,#eef2f7 0%,#f8fafc 55%,#e8f0f7 100%)",
        }}
      >
        {/* dot-grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: dark
              ? "radial-gradient(rgba(255,255,255,0.055) 1px,transparent 1px)"
              : "radial-gradient(rgba(0,79,115,0.10) 1px,transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* ambient orbs */}
        <div className="absolute pointer-events-none" style={{
          top:"8%",left:"4%",width:480,height:480,borderRadius:"50%",
          background: dark
            ? "radial-gradient(circle,rgba(0,79,115,.28) 0%,transparent 70%)"
            : "radial-gradient(circle,rgba(0,79,115,.12) 0%,transparent 70%)",
          animation:"orb-drift 14s ease-in-out infinite",
        }} />
        <div className="absolute pointer-events-none" style={{
          bottom:"6%",right:"3%",width:360,height:360,borderRadius:"50%",
          background: dark
            ? "radial-gradient(circle,rgba(26,127,168,.20) 0%,transparent 70%)"
            : "radial-gradient(circle,rgba(26,127,168,.10) 0%,transparent 70%)",
          animation:"orb-drift-2 18s ease-in-out infinite",
        }} />

        {/* ── card ─────────────────────────────────────────────────────────── */}
        <div
          className="relative w-full max-w-[640px] rounded-3xl overflow-hidden transition-all duration-500"
          style={dark ? {
            background:"rgba(255,255,255,0.035)",
            backdropFilter:"blur(28px) saturate(1.4)",
            WebkitBackdropFilter:"blur(28px) saturate(1.4)",
            border:"1px solid rgba(255,255,255,0.10)",
            boxShadow:"0 8px 60px rgba(0,0,0,.55),inset 0 1px 0 rgba(255,255,255,.08)",
          } : {
            background:"#ffffff",
            border:"1px solid rgba(0,79,115,0.12)",
            boxShadow:"0 8px 48px rgba(0,79,115,.10),0 1px 4px rgba(0,0,0,.06)",
          }}
        >
          {/* top edge glow line */}
          <div className="h-px w-full" style={{
            background: dark
              ? "linear-gradient(90deg,transparent,rgba(26,127,168,.9),rgba(0,79,115,.6),transparent)"
              : "linear-gradient(90deg,transparent,rgba(0,79,115,.6),rgba(26,127,168,.4),transparent)",
          }} />

          <div className="px-8 pt-7 pb-8">

            {/* badge row + toggle */}
            <div className="wu-a flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                {tenantLogo ? (
                  <img
                    src={tenantLogo}
                    alt=""
                    className="h-8 w-auto max-w-[120px] object-contain"
                  />
                ) : null}
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest"
                  style={dark ? {
                    background:"linear-gradient(135deg,rgba(0,79,115,.6),rgba(26,127,168,.4))",
                    border:"1px solid rgba(26,127,168,.4)",color:"rgba(103,232,249,0.92)",
                  } : {
                    background:"rgba(0,79,115,0.08)",border:"1px solid rgba(0,79,115,0.20)",color:"#004f73",
                  }}
                >
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full inline-block"
                    style={{ background: dark ? "#67e8f9" : "#004f73" }} />
                  AI-Powered
                </span>
                <span className="text-[11px] font-semibold tracking-widest uppercase"
                  style={{ color: dark ? "rgba(255,255,255,0.28)" : "rgba(0,79,115,0.45)" }}>
                  {trackLabel}
                </span>
              </div>

              <button
                type="button"
                onClick={toggleTheme}
                title={dark ? "Switch to light mode" : "Switch to dark mode"}
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium transition-all duration-200"
                style={dark ? {
                  background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.12)",
                  color:"rgba(255,255,255,0.50)",
                } : {
                  background:"rgba(0,79,115,0.07)",border:"1px solid rgba(0,79,115,0.18)",
                  color:"rgba(0,79,115,0.65)",
                }}
              >
                {dark ? <IconSun /> : <IconMoon />}
                {dark ? "Light" : "Dark"}
              </button>
            </div>

            {/* greeting */}
            <div className="wu-a wu-a1 mb-4">
              <h1
                className="text-[2rem] font-bold leading-tight tracking-tight mb-1.5"
                style={{ color: dark ? "#ffffff" : "#0f172a" }}
              >
                {firstName ? <>Hi {firstName},</> : "Welcome"}
              </h1>

              <p className="text-[14.5px] leading-relaxed mb-1.5"
                style={{ color: dark ? "rgba(255,255,255,0.70)" : "#374151" }}>
                You are about to take the{" "}
                <span className="font-semibold" style={{ color: dark ? "#ffffff" : accent }}>
                  {productName(assessmentType, branding)}
                </span>
                {companyName ? (
                  <> for{" "}
                    <span className="font-semibold"
                      style={{ color: dark ? "#67e8f9" : accent }}>
                      {companyName}
                    </span>
                  </>
                ) : null}.
              </p>

              <p className="text-[12px] leading-relaxed"
                style={{ color: dark ? "rgba(255,255,255,0.35)" : "#6b7280" }}>
                {welcomeDescription(assessmentType)}
              </p>
            </div>

            {/* stat pills */}
            <div className="wu-a wu-a2 flex flex-wrap gap-2 mb-5">
              {[
                { icon: <IconDoc />, text: `${questions} questions` },
                { icon: <IconClock />, text: `~${minutes} min` },
              ].map(({ icon, text }) => (
                <span key={text}
                  className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[13px] font-medium"
                  style={dark ? {
                    background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",
                    backdropFilter:"blur(8px)",color:"rgba(255,255,255,0.65)",
                  } : {
                    background:"rgba(0,79,115,0.06)",border:"1px solid rgba(0,79,115,0.15)",color:"#004f73",
                  }}
                >
                  {icon}{text}
                </span>
              ))}
            </div>

            {/* feature rows */}
            <div className="wu-a wu-a3 space-y-2 mb-6">
              {[
                {
                  icon: <span style={{ color: dark ? "#67e8f9" : "#004f73" }}><IconSparkle /></span>,
                  main: "AI guide", rest: " helps clarify every statement in real time.",
                },
                {
                  icon: <span style={{ color: dark ? "rgba(103,232,249,0.75)" : "#005f8b" }}><IconCheck /></span>,
                  main: "Pause & return", rest: " any time — your progress is saved to this link.",
                },
                ...(showLiteUpgradeNote ? [{
                  icon: <span style={{ color: dark ? "rgba(103,232,249,0.75)" : "#005f8b" }}><IconCheck /></span>,
                  main: typeTrackLabel(assessmentType, "full"), rest: " available as an optional next step after the Lite.",
                }] : []),
              ].map(({ icon, main, rest }, i) => (
                <div key={i}
                  className="flex items-start gap-3 rounded-xl px-4 py-2.5"
                  style={dark ? {
                    background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
                  } : {
                    background:"rgba(0,79,115,0.04)",border:"1px solid rgba(0,79,115,0.09)",
                  }}
                >
                  <span className="flex-shrink-0 mt-0.5">{icon}</span>
                  <span className="text-[13px] leading-snug"
                    style={{ color: dark ? "rgba(255,255,255,0.58)" : "#4b5563" }}>
                    <span className="font-semibold"
                      style={{ color: dark ? "rgba(255,255,255,0.88)" : "#111827" }}>
                      {main}
                    </span>
                    {rest}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            {showPreSurvey ? (
              <div className="wu-a wu-a4 space-y-2.5">
                <button
                  type="button"
                  className={`group w-full flex items-center justify-center gap-2.5 rounded-2xl py-3.5 text-white font-bold text-[15px] tracking-wide transition-all duration-200 cta-shimmer ${dark ? "cta-shimmer-dark" : "cta-shimmer-light"}`}
                  onClick={() => onPreSurvey?.()}
                >
                  Complete company benchmark
                  <IconArrow />
                </button>
                <button
                  type="button"
                  className="w-full rounded-2xl py-3 text-sm font-semibold transition-colors"
                  style={
                    dark
                      ? {
                          color: "rgba(255,255,255,0.72)",
                          border: "1px solid rgba(255,255,255,0.14)",
                          background: "rgba(255,255,255,0.04)",
                        }
                      : {
                          color: "#004f73",
                          border: "1px solid rgba(0,79,115,0.18)",
                          background: "rgba(0,79,115,0.04)",
                        }
                  }
                  onClick={handleStart}
                >
                  Start assessment now — skip benchmark
                </button>
                <p
                  className="text-center text-[11px] leading-snug"
                  style={{ color: dark ? "rgba(255,255,255,0.30)" : "#94a3b8" }}
                >
                  You can complete the company benchmark before or after the full assessment.
                </p>
              </div>
            ) : (
              <button
                type="button"
                className={`wu-a wu-a4 group w-full flex items-center justify-center gap-2.5 rounded-2xl py-3.5 text-white font-bold text-[15px] tracking-wide transition-all duration-200 cta-shimmer ${dark ? "cta-shimmer-dark" : "cta-shimmer-light"}`}
                onClick={handleStart}
              >
                {ctaLabel}
                <IconArrow />
              </button>
            )}

            {/* identity footnote */}
            {(userData?.name || userData?.email) && (
              <p className="mt-4 text-center text-[11px]"
                style={{ color: dark ? "rgba(255,255,255,0.22)" : "#9ca3af" }}>
                Responding as{" "}
                <span className="font-medium"
                  style={{ color: dark ? "rgba(255,255,255,0.42)" : "#6b7280" }}>
                  {[userData.name, userData.email].filter(Boolean).join(" · ")}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
